import { useGetUserRatingEdges } from "@/api/admin/useGetUserRatingEdges";
import { Badge, Group, Tooltip } from "@mantine/core";
import { StagingGroup } from "../StagingGroupCard/types";
import {
  getGroupRatingRelations,
  getMemberRatingsGiven,
  getMemberUids,
  RatingRelation,
} from "../utils/ratingCompatibility";

const ANONYMOUS_DISPLAY_NAME = "익명의 게스트";

// A member rarely rates more than a couple of their own groupmates, so capping keeps one
// unusually opinionated guest from pushing the row's other details off screen.
const MAX_VISIBLE_CHIPS = 3;

type RatingTone = {
  color: string;
  /** Reads as a sentence about a rating, e.g. "민준님을 아쉬워했어요". */
  sentenceVerb: string;
  /** Reads as a count, e.g. "나쁜 평가 2건". */
  countNoun: string;
};

const DISLIKE_TONE: RatingTone = {
  color: "red",
  sentenceVerb: "아쉬워했어요",
  countNoun: "나쁜 평가",
};

const LIKE_TONE: RatingTone = {
  color: "green",
  sentenceVerb: "좋아했어요",
  countNoun: "좋은 평가",
};

type DisplayNames = Map<string, string>;

/**
 * Whether this group holds any ratings at all, as a count of pairs rather than of ratings so
 * that a mutual opinion and a pair who have now met twice each count once.
 *
 * The detail lives on the member rows; this is only here so a group with friction can be
 * spotted while scanning cards, without reading every row. Most pending guests have never
 * met, so showing nothing is the normal case and renders nothing rather than an all-clear.
 */
export const GroupRatingCapsules = ({ stagingGroup }: { stagingGroup: StagingGroup }) => {
  const memberUids = getMemberUids(stagingGroup);
  const { data, isFetching } = useGetUserRatingEdges(memberUids);

  const { likedRelations, dislikedRelations } = getGroupRatingRelations(
    memberUids,
    data?.ratings ?? []
  );

  if (likedRelations.length === 0 && dislikedRelations.length === 0) {
    return null;
  }

  const displayNames = buildDisplayNames(stagingGroup);

  return (
    <Group gap={6} opacity={isFetching ? 0.5 : 1}>
      <RelationCountBadge
        relations={dislikedRelations}
        tone={DISLIKE_TONE}
        displayNames={displayNames}
      />
      <RelationCountBadge relations={likedRelations} tone={LIKE_TONE} displayNames={displayNames} />
    </Group>
  );
};

/**
 * The groupmates this member rated, named on this member's own row.
 *
 * Because the chip sits on the rater's row and carries the rated guest's name, "민준's row
 * shows a red 서연" reads as 민준 being disappointed by 서연 with no arrow needed. The flip
 * side is that being disliked by someone shows up on their row, not this one.
 */
export const MemberRatingCapsules = ({
  uid,
  stagingGroup,
}: {
  uid: string;
  stagingGroup: StagingGroup;
}) => {
  const memberUids = getMemberUids(stagingGroup);
  const { data, isFetching } = useGetUserRatingEdges(memberUids);

  const { liked, disliked } = getMemberRatingsGiven(uid, memberUids, data?.ratings ?? []);

  if (liked.length === 0 && disliked.length === 0) {
    return null;
  }

  const displayNames = buildDisplayNames(stagingGroup);

  return (
    <Group gap={4} wrap="nowrap" opacity={isFetching ? 0.5 : 1}>
      <NameChips uids={disliked} tone={DISLIKE_TONE} displayNames={displayNames} />
      <NameChips uids={liked} tone={LIKE_TONE} displayNames={displayNames} />
    </Group>
  );
};

const NameChips = ({
  uids,
  tone,
  displayNames,
}: {
  uids: string[];
  tone: RatingTone;
  displayNames: DisplayNames;
}) => {
  if (uids.length === 0) {
    return null;
  }

  const visible = uids.slice(0, MAX_VISIBLE_CHIPS);
  const hidden = uids.slice(MAX_VISIBLE_CHIPS);

  return (
    <>
      {visible.map((counterpartUid) => (
        <Tooltip
          key={counterpartUid}
          label={describeRating(counterpartUid, tone, displayNames)}
          withArrow
        >
          <Badge color={tone.color} variant="light" size="xs" style={{ cursor: "help" }}>
            {toDisplayName(counterpartUid, displayNames)}
          </Badge>
        </Tooltip>
      ))}
      {hidden.length > 0 && (
        <Tooltip
          label={hidden
            .map((counterpartUid) => describeRating(counterpartUid, tone, displayNames))
            .join("\n")}
          multiline
          maw={280}
          withArrow
        >
          <Badge color={tone.color} variant="outline" size="xs" style={{ cursor: "help" }}>
            +{hidden.length}
          </Badge>
        </Tooltip>
      )}
    </>
  );
};

const RelationCountBadge = ({
  relations,
  tone,
  displayNames,
}: {
  relations: RatingRelation[];
  tone: RatingTone;
  displayNames: DisplayNames;
}) => {
  if (relations.length === 0) {
    return null;
  }

  const tooltip = relations
    .map((relation) => describeRelation(relation, tone, displayNames))
    .join("\n");

  return (
    <Tooltip label={tooltip} multiline maw={280} withArrow>
      <Badge color={tone.color} variant="light" size="sm" style={{ cursor: "help" }}>
        {tone.countNoun} {relations.length}건
      </Badge>
    </Tooltip>
  );
};

const buildDisplayNames = (stagingGroup: StagingGroup): DisplayNames =>
  new Map(
    stagingGroup.stagedRequests.map((request) => [
      request.user.uid,
      request.user.nickname ?? request.user.name ?? ANONYMOUS_DISPLAY_NAME,
    ])
  );

const toDisplayName = (uid: string, displayNames: DisplayNames): string =>
  displayNames.get(uid) ?? ANONYMOUS_DISPLAY_NAME;

const describeRating = (uid: string, tone: RatingTone, displayNames: DisplayNames): string =>
  `이 게스트가 ${toDisplayName(uid, displayNames)}님을 ${tone.sentenceVerb}`;

const describeRelation = (
  relation: RatingRelation,
  tone: RatingTone,
  displayNames: DisplayNames
): string => {
  const raterName = toDisplayName(relation.raterId, displayNames);
  const ratedName = toDisplayName(relation.ratedUserId, displayNames);

  if (relation.isMutual) {
    return `${raterName}님과 ${ratedName}님이 서로 ${tone.sentenceVerb}`;
  }

  return `${raterName}님이 ${ratedName}님을 ${tone.sentenceVerb}`;
};
